import axios from "axios";
import type { z } from "zod";

import { attachAuthInterceptors } from "../auth/authFailure";
import { looseArray, looseParse } from "../looseParse";
import {
  acatsAssetSchema,
  acatsTransferSchema,
  type assetValidationResultSchema,
  type assetValidationSummarySchema,
  type cashBalanceSchema,
  type contraAccountDetailsSchema,
  type contraBrokerSchema,
  type contraTransferDetailsSchema,
  type equitySchema,
  getListAcatsTransfersForAccountResponseSchema,
  getListContraBrokersResponseSchema,
  postAcatsTransferResponseSchema,
  type securitiesSchema,
  validateAcatsAssetsByCusipResponseSchema,
} from "./acats.schema";

const instance = axios.create({ baseURL: window?.env?.ACATS_API_URL });

attachAuthInterceptors(instance);

const isApiObject = (data: unknown): data is Record<string, unknown> =>
  typeof data === "object" && data !== null && !Array.isArray(data);

export * from "./acats.constants";

export type AcatsTransfer = z.infer<typeof acatsTransferSchema>;

export type GetListAcatsTransfersForAccountResponse = z.infer<
  typeof getListAcatsTransfersForAccountResponseSchema
>;

export const getListAcatsTransfersForAccount = async (
  accountId: string,
  pageToken?: string,
): Promise<GetListAcatsTransfersForAccountResponse> => {
  const res = await instance.get<GetListAcatsTransfersForAccountResponse>(
    `/v1beta1/acats/${accountId}`,
    { params: { page_token: pageToken } },
  );
  const payload = isApiObject(res.data) ? res.data : { transfers: [] };
  return looseParse(
    getListAcatsTransfersForAccountResponseSchema,
    payload,
    "getListAcatsTransfersForAccount",
  );
};

export const getAcatsTransferDetails = async (
  accountId: string,
  acatsId: string,
): Promise<AcatsTransfer> => {
  const res = await instance.get<AcatsTransfer>(
    `/v1beta1/acats/${accountId}/${acatsId}`,
  );
  return looseParse(acatsTransferSchema, res.data, "getAcatsTransferDetails");
};

export type AcatsAsset = z.infer<typeof acatsAssetSchema>;

export const getListAssetsForAcatsTransfer = async (
  accountId: string,
  acatsId: string,
): Promise<AcatsAsset[]> => {
  const res = await instance.get<AcatsAsset[]>(
    `/v1beta1/acats/${accountId}/${acatsId}/assets`,
  );
  return looseParse(
    looseArray(acatsAssetSchema, "getListAssetsForAcatsTransfer"),
    res.data,
    "getListAssetsForAcatsTransfer",
  );
};

export type ContraBroker = z.infer<typeof contraBrokerSchema>;

export type GetListContraBrokersResponse = z.infer<
  typeof getListContraBrokersResponseSchema
>;

export const getListContraBrokers =
  async (): Promise<GetListContraBrokersResponse> => {
    const res = await instance.get<GetListContraBrokersResponse>(
      `/v1beta1/acats/contrabrokers`,
    );
    return looseParse(
      getListContraBrokersResponseSchema,
      res.data,
      "getListContraBrokers",
    );
  };

export type ContraAccountDetails = z.infer<typeof contraAccountDetailsSchema>;

export type CashBalance = z.infer<typeof cashBalanceSchema>;

export type Equity = z.infer<typeof equitySchema>;

export type Securities = z.infer<typeof securitiesSchema>;

export type ContraTransferDetails = z.infer<typeof contraTransferDetailsSchema>;

export type PostAcatsTransferRequest = {
  contra_account_details: ContraAccountDetails;
  transfer_details: ContraTransferDetails;
  ip_address: string;
  signed_at: string;
};

export type PostAcatsTransferResponse = z.infer<
  typeof postAcatsTransferResponseSchema
>;

export const postAcatsTransfer = async (
  accountId: string,
  transfer: PostAcatsTransferRequest,
) => {
  const res = await instance.post<PostAcatsTransferResponse>(
    `/v1beta1/acats/${accountId}`,
    transfer,
  );
  return looseParse(
    postAcatsTransferResponseSchema,
    res.data,
    "postAcatsTransfer",
  );
};

export type AssetValidationResult = z.infer<typeof assetValidationResultSchema>;

export type AssetValidationSummary = z.infer<
  typeof assetValidationSummarySchema
>;

export type ValidateAcatsAssetsByCusipResponse = z.infer<
  typeof validateAcatsAssetsByCusipResponseSchema
>;

export const validateAcatsAssetsByCusip = async (cusips: string[]) => {
  const res = await instance.post<ValidateAcatsAssetsByCusipResponse>(
    `/v1beta1/acats/assets/validate`,
    { cusips },
  );
  return looseParse(
    validateAcatsAssetsByCusipResponseSchema,
    res.data,
    "validateAcatsAssetsByCusip",
  );
};

/**
 * Fetch all non-settled/rejected ACATS transfers for all accounts
 * Handles pagination automatically and filters out completed transfers
 */
export const getAllNonCompletedAcatsTransfers = async (
  accountIds: string[],
): Promise<AcatsTransfer[]> => {
  const allTransfers: AcatsTransfer[] = [];

  for (const accountId of accountIds) {
    let pageToken: string | undefined;

    do {
      try {
        const response = await getListAcatsTransfersForAccount(
          accountId,
          pageToken,
        );

        // `response.transfers` is normally guaranteed by the schema, but
        // `looseParse` falls back to the raw (possibly shapeless) payload on
        // a whole-envelope validation failure - guard the dereference so a
        // malformed page can't throw and abort every remaining account/page.
        const transfers = Array.isArray(response.transfers)
          ? response.transfers
          : [];

        // Filter out settled and rejected transfers
        const activeTransfers = transfers.filter(
          (transfer) =>
            transfer.status?.transfer_status !== "SETTLED" &&
            transfer.status?.transfer_status !== "REJECTED",
        );

        allTransfers.push(...activeTransfers);
        pageToken = response.page_token_next;
      } catch (error) {
        console.error(
          `Failed to fetch ACATS transfers for account ${accountId}:`,
          error,
        );
        // Continue with next account instead of failing completely
        break;
      }
    } while (pageToken);
  }

  return allTransfers;
};
